/** Term-end grades popup state — pure policy + persistent store.
 *  Owner bug: the "seen" marker lived in sessionStorage, so the grades popup
 *  re-popped on EVERY visit. Now: persistent per-term state with a policy —
 *  graded = never again (for that end date); dismissed = re-arms after
 *  REPROMPT_DAYS or when the term's end date changes. */

export const TERMEND_KEY = "nelurio.termend.state.v1";
export const REPROMPT_DAYS = 7;

export interface TermEndCandidate {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
}

export type TermEndStatus = "dismissed" | "graded";

export interface TermEndStateEntry {
  id: string;
  end: string;
  status: TermEndStatus;
  at: number;
}

/** The effective end timestamp of a term: endsOn wins; otherwise startsOn +
 *  180 days. Date-only strings parse at local noon (T12:00:00) — UTC midnight
 *  made the report fire during the term's last day in western timezones. */
export function termEndDeadline(term: { startsOn: string; endsOn: string }): number | null {
  if (term.endsOn) {
    const parsed = new Date(`${term.endsOn}T12:00:00`).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (term.startsOn) {
    const parsed = new Date(`${term.startsOn}T12:00:00`).getTime();
    return Number.isFinite(parsed) ? parsed + 180 * 86_400_000 : null;
  }
  return null;
}

export function isTermEnded(term: { startsOn: string; endsOn: string }, now: number): boolean {
  const deadline = termEndDeadline(term);
  return deadline !== null && deadline < now;
}

export function readTermEndState(store: Pick<Storage, "getItem"> | null = typeof window === "undefined" ? null : window.localStorage): TermEndStateEntry[] {
  try {
    const value = JSON.parse(store?.getItem(TERMEND_KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((entry) => entry && typeof entry.id === "string" && typeof entry.end === "string") : [];
  } catch {
    return [];
  }
}

export function markTermEnd(store: Pick<Storage, "getItem" | "setItem"> | null, id: string, end: string, status: TermEndStatus, now: number): TermEndStateEntry[] {
  const others = readTermEndState(store).filter((entry) => !(entry.id === id && entry.end === end));
  const next = [...others, { id, end, status, at: now }];
  try { store?.setItem(TERMEND_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  return next;
}

/** Which candidate (if any) should pop now:
 *  - must be ENDED,
 *  - skip ones already GRADED for this end date,
 *  - skip ones DISMISSED within REPROMPT_DAYS for this end date,
 *  - a changed end date re-arms the candidate. */
export function pickTermEnd(
  candidates: TermEndCandidate[],
  state: TermEndStateEntry[],
  now: number
): TermEndCandidate | null {
  for (const candidate of candidates) {
    const deadline = termEndDeadline(candidate);
    if (deadline === null || deadline >= now) continue;
    const entries = state.filter((entry) => entry.id === candidate.id && entry.end === candidate.endsOn);
    if (entries.some((entry) => entry.status === "graded")) continue;
    const dismissed = entries.filter((entry) => entry.status === "dismissed");
    const lastDismiss = dismissed.length ? Math.max(...dismissed.map((entry) => entry.at)) : -Infinity;
    if (now - lastDismiss < REPROMPT_DAYS * 86_400_000) continue;
    return candidate;
  }
  return null;
}
